import React, { useState, useEffect, useCallback } from "react";
import { Table, Form, Row, Col, Card, Button, Spinner, Alert } from "react-bootstrap";
import { FaBook, FaSearch, FaFilter, FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import firebaseApp from "../../../firebase/credenciales";
import { getFirestore, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { periodoActual, toNum } from "../utils";

const db = getFirestore(firebaseApp);

const BTN = { background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", border: "none", color: "white" };

const LibroMayor = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [periodo, setPeriodo] = useState(periodoActual());
  const [targetCuenta, setTargetCuenta] = useState("104101");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [movimientos, setMovimientos] = useState([]);
  const [cuentasDisponibles, setCuentasDisponibles] = useState([]);

  // Cargar cuentas únicas presentes en gco_asientos para el select
  const cargarCuentas = useCallback(async () => {
    if (!rucEmpresa) return;
    try {
      const q = query(collection(db, "gco_asientos"), where("ruc_empresa", "==", rucEmpresa));
      const snap = await getDocs(q);
      const cuentasSet = new Set();
      snap.docs.forEach(doc => {
        const data = doc.data();
        (data.debe || []).forEach(l => l.cuenta && cuentasSet.add(l.cuenta));
        (data.haber || []).forEach(l => l.cuenta && cuentasSet.add(l.cuenta));
      });
      setCuentasDisponibles([...cuentasSet].sort());
    } catch (e) {
      console.error(e);
    }
  }, [rucEmpresa]);

  const cargarMovimientos = useCallback(async () => {
    if (!rucEmpresa || !targetCuenta) return;
    try {
      setLoading(true);
      setError("");
      
      const q = query(
        collection(db, "gco_asientos"),
        where("ruc_empresa", "==", rucEmpresa),
        orderBy("fecha", "asc")
      );
      const snap = await getDocs(q);
      
      let saldoAcumulado = 0;
      const firstDigit = targetCuenta.charAt(0);
      // Cuentas de pasivo, patrimonio y ventas incrementan con Haber y decrementan con Debe
      const esAcreedora = ["4", "5", "7"].includes(firstDigit);
      
      const movs = [];
      snap.docs.forEach(doc => {
        const a = doc.data();
        // Filtrar por periodo si se especifica
        if (periodo && !String(a.fecha).startsWith(periodo)) return;

        (a.debe || []).forEach(l => {
          if (l.cuenta === targetCuenta) {
            movs.push({
              id: `${doc.id}_debe`,
              fecha: a.fecha,
              cuo: a.cuo || "—",
              glosa: a.glosa,
              debe: toNum(l.monto),
              haber: 0
            });
          }
        });

        (a.haber || []).forEach(l => {
          if (l.cuenta === targetCuenta) {
            movs.push({
              id: `${doc.id}_haber`,
              fecha: a.fecha,
              cuo: a.cuo || "—",
              glosa: a.glosa,
              debe: 0,
              haber: toNum(l.monto)
            });
          }
        });
      });

      // Ordenar por fecha y luego por CUO para consistencia del saldo acumulado
      movs.sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)) || String(a.cuo).localeCompare(String(b.cuo)));

      // Calcular saldo acumulado paso a paso
      const calculated = movs.map(m => {
        if (esAcreedora) {
          saldoAcumulado += (m.haber - m.debe);
        } else {
          saldoAcumulado += (m.debe - m.haber);
        }
        return {
          ...m,
          saldo: Number(saldoAcumulado.toFixed(2))
        };
      });

      setMovimientos(calculated);
    } catch (e) {
      console.error(e);
      setError("Error cargando los movimientos del Libro Mayor.");
    } finally {
      setLoading(false);
    }
  }, [rucEmpresa, targetCuenta, periodo]);

  useEffect(() => {
    cargarCuentas();
  }, [cargarCuentas]);

  useEffect(() => {
    cargarMovimientos();
  }, [cargarMovimientos]);

  const totalDebe = movimientos.reduce((sum, m) => sum + m.debe, 0);
  const totalHaber = movimientos.reduce((sum, m) => sum + m.haber, 0);
  const finalSaldo = movimientos.length > 0 ? movimientos[movimientos.length - 1].saldo : 0;

  const exportarExcel = () => {
    const rows = movimientos.map(m => ({
      Fecha: m.fecha,
      CUO: m.cuo,
      Glosa: m.glosa,
      Debe: m.debe.toFixed(2),
      Haber: m.haber.toFixed(2),
      Saldo: m.saldo.toFixed(2)
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, `Cuenta_${targetCuenta}`);
    XLSX.writeFile(wb, `Libro_Mayor_${rucEmpresa}_${targetCuenta}.xlsx`);
  };

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError("")} dismissible className="border-0 shadow-sm">{error}</Alert>}

      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <div className="d-flex align-items-center mb-3">
            <FaBook className="text-secondary me-2" size={20} />
            <h5 className="mb-0 fw-semibold">Filtros del Libro Mayor</h5>
          </div>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-muted">Seleccionar Cuenta Contable</Form.Label>
                <Form.Select className="fw-bold" value={targetCuenta} onChange={(e) => setTargetCuenta(e.target.value)}>
                  {cuentasDisponibles.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  {cuentasDisponibles.length === 0 && (
                    <option value="104101">104101 - Bancos (Por defecto)</option>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-muted">Periodo</Form.Label>
                <Form.Control type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={5} className="text-end">
              <Button variant="outline-success" className="rounded-pill px-3 fw-bold btn-sm me-2" onClick={exportarExcel} disabled={loading}><FaFileExcel /> Excel</Button>
              <Button style={BTN} className="rounded-pill px-3 btn-sm" onClick={cargarMovimientos} disabled={loading}><FaSearch /> Consultar</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Resumen de Totales */}
      <Row className="mb-4 g-3">
        <Col md={4}>
          <Card className="border-0 shadow-sm text-center py-3 bg-white" style={{ borderRadius: "15px" }}>
            <h6 className="text-muted small mb-1">Total Debe</h6>
            <h4 className="fw-bold text-success mb-0">S/ {totalDebe.toFixed(2)}</h4>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm text-center py-3 bg-white" style={{ borderRadius: "15px" }}>
            <h6 className="text-muted small mb-1">Total Haber</h6>
            <h4 className="fw-bold text-danger mb-0">S/ {totalHaber.toFixed(2)}</h4>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm text-center py-3 bg-white" style={{ borderRadius: "15px" }}>
            <h6 className="text-muted small mb-1">Saldo Final Acumulado</h6>
            <h4 className="fw-bold text-primary mb-0">S/ {finalSaldo.toFixed(2)}</h4>
          </Card>
        </Col>
      </Row>

      {/* Tabla del Mayor */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
        <div className="table-responsive">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="secondary" /><p className="mt-3 text-muted">Calculando saldos del Libro Mayor...</p></div>
          ) : movimientos.length === 0 ? (
            <div className="text-center py-5">
              <FaBook size="4rem" className="text-muted opacity-25 mb-3" />
              <h5 className="text-muted fw-semibold">No se encontraron movimientos para esta cuenta contable</h5>
            </div>
          ) : (
            <Table hover className="align-middle bg-white mb-0" style={{ fontSize: "0.95rem" }}>
              <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                <tr>
                  <th className="py-3 px-4 text-secondary fw-semibold">Fecha</th>
                  <th className="py-3 px-4 text-secondary fw-semibold">CUO</th>
                  <th className="py-3 px-4 text-secondary fw-semibold">Glosa</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-end">Debe</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-end">Haber</th>
                  <th className="py-3 px-4 text-secondary fw-semibold text-end">Saldo Acumulado</th>
                </tr>
              </thead>
              <tbody>
                {movimientos.map((m) => (
                  <tr key={m.id}>
                    <td className="px-4 py-3 text-dark fw-medium">{m.fecha}</td>
                    <td className="px-4 py-3 fw-bold text-primary">{m.cuo}</td>
                    <td className="px-4 py-3 text-muted">{m.glosa}</td>
                    <td className="px-4 py-3 text-end fw-semibold text-success">{m.debe > 0 ? m.debe.toFixed(2) : "—"}</td>
                    <td className="px-4 py-3 text-end fw-semibold text-danger">{m.haber > 0 ? m.haber.toFixed(2) : "—"}</td>
                    <td className="px-4 py-3 text-end fw-bold text-primary">S/ {m.saldo.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
};

export default LibroMayor;
